import { uniq } from 'lodash';
import { graphql, OptionProps } from 'react-apollo';
import configureApolloClient from '../../../apollo/configureApolloClient';
import {
    ActivityChartContainerProps,
    ActivityMapContainerProps,
    ActivityPayload,
    ActivityStreamInput,
    CustomStreamType,
    FrontPageActivityMapVariables,
    ScalarStream,
    StravaResolution,
    StravaStreamType
} from '../types';
import {
    DEFAULT_RESOLUTION,
    withMapOrChartStreamsCore
} from '../withActivityStreams';
import {
    DemoActivityChartQuery,
    DemoActivityMapQuery,
    FrontPageActivityMapQuery
} from './queries';

export const withFrontPageMapStreams = graphql<
    ActivityMapContainerProps,
    { frontPageActivity: ActivityPayload },
    FrontPageActivityMapVariables,
    ActivityMapContainerProps
>(FrontPageActivityMapQuery, {
    options: (props: ActivityMapContainerProps) => ({
        variables: {
            streamTypes: uniq([
                ...props.xAxisStreamTypes,
                ...(props.yAxisStreamType ? [props.yAxisStreamType] : []),
                StravaStreamType.distance,
                StravaStreamType.time
            ])
        }
    }),
    props: (
        props: OptionProps<
            ActivityMapContainerProps,
            { frontPageActivity: ActivityPayload }
        >
    ): ActivityMapContainerProps => {
        const { data, ownProps } = props;

        if (!data) {
            throw new Error('No data provided');
        }

        const { error, loading } = data;
        const payload = data.frontPageActivity;

        return {
            ...ownProps,
            activity: payload ? payload.activity : undefined,
            error,
            loading
        };
    }
});

export async function fetchDemoActivityStreams(props: ActivityStreamInput) {
    const client = configureApolloClient();
    const { types, selectedStreamType, id, resolution } = props;

    if (!types) {
        throw new Error('No stream types specified');
    }

    const xAxisTypes = [StravaStreamType.distance, StravaStreamType.time];

    const map = client.query({
        query: DemoActivityMapQuery,
        variables: {
            input: {
                id: id.toString()
            },
            resolution: resolution || DEFAULT_RESOLUTION,
            streamTypes: selectedStreamType
                ? uniq([selectedStreamType, ...xAxisTypes]).sort()
                : xAxisTypes
        }
    });

    const charts = types
        .filter(t => xAxisTypes.indexOf(t as StravaStreamType) === -1)
        .map(t =>
            client.query({
                query: DemoActivityChartQuery,
                variables: {
                    input: {
                        id: id.toString()
                    },
                    resolution: resolution || DEFAULT_RESOLUTION,
                    streamTypes: uniq([t, ...xAxisTypes]).sort()
                }
            })
        );

    await Promise.all<any>([map, ...charts]);
}

export async function fetchDemoScalarStreams({
    id,
    resolution,
    streamTypes
}: {
    id: string;
    resolution: StravaResolution;
    streamTypes: (StravaStreamType | CustomStreamType)[];
}) {
    const client = configureApolloClient();

    const results = await client.query<{
        activity: { activity: { scalarStreams: ScalarStream[] } };
    }>({
        query: DemoActivityChartQuery,
        variables: {
            input: { id },
            streamTypes: streamTypes.slice().sort()
        }
    });
    return results;
}

export const withDemoMapStreams = withMapOrChartStreamsCore<
    ActivityMapContainerProps
>(DemoActivityMapQuery, 'demoActivity');

export const withDemoChartStreams = withMapOrChartStreamsCore<
    ActivityChartContainerProps
>(DemoActivityChartQuery, 'demoActivity');



// WEBPACK FOOTER //
// ./src/client/components/Strava/Demo/withDemoActivityStreams.tsx